import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { useSiteMode } from "@/context/SiteModeContext";
import { toast } from "sonner";
import { CheckCircle2, Clock, Copy, Globe, Loader2, RefreshCw, ShieldCheck } from "lucide-react";
import BackOffice from "@/components/account/BackOffice";
import { Btn, Chip, PageHead, Section, inputCls } from "@/components/account/Shell";
import api, { formatApiError } from "@/lib/api";

const STATUS = {
    none: ["neutral", "no domain yet"],
    pending: ["amber", "waiting on DNS"],
    verified: ["blue", "points here"],
    live: ["green", "live"],
};

const when = (iso) => { try { return new Date(iso).toLocaleString(); } catch { return ""; } };

/** Back office → Domain: the free address, your own name, and the exact records to paste. */
function ClassicStoreDomain() {
    const [d, setD] = useState(null);
    const [sub, setSub] = useState("");
    const [host, setHost] = useState("");
    const [busy, setBusy] = useState("");

    const apply = (data) => {
        setD(data);
        setSub(data.subdomain || "");
        setHost(data.custom_domain || "");
        if (data.note) toast.success(data.note);
    };

    useEffect(() => {
        api.get("/store/domain").then(({ data }) => { setD(data); setSub(data.subdomain || ""); setHost(data.custom_domain || ""); })
            .catch((e) => setD({ error: formatApiError(e.response?.data?.detail) }));
    }, []);

    const run = async (job, fn) => {
        setBusy(job);
        try { const { data } = await fn(); apply(data); }
        catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
        finally { setBusy(""); }
    };

    const copy = (value) => {
        navigator.clipboard?.writeText(value);
        toast.success(`Copied ${value}`);
    };

    if (!d) return <BackOffice><div className="flex justify-center py-20"><Loader2 className="animate-spin text-[#4a4a4a]" /></div></BackOffice>;
    if (d.error) return (
        <BackOffice>
            <PageHead kicker="Domain" title="A domain points at a store" sub={d.error} testid="domain-locked" />
        </BackOffice>
    );

    const [tone, label] = STATUS[d.status] || STATUS.none;

    return (
        <BackOffice>
            <div className="space-y-3" data-testid="domain-page">
                <PageHead kicker="Profile" title="Your web address"
                    sub="Every shop gets a free address the day it opens. Bring your own name whenever you're ready."
                    right={<Chip tone={tone} testid="domain-status">{label}</Chip>} testid="domain-head" />

                <Section title="The free one" note="Live right now — nothing to set up."
                    testid="domain-free">
                    <div className="flex flex-wrap items-end gap-2">
                        <div>
                            <label className="mb-1 block text-[13px] font-semibold text-[#1a1a1a]">Address</label>
                            <div className="flex items-center">
                                <input className={inputCls} value={sub} placeholder={d.suggested_subdomain}
                                    onChange={(e) => setSub(e.target.value)} data-testid="domain-subdomain" />
                                <span className="shrink-0 border border-l-0 border-[#d0d0d0] bg-[#f5f5f5] px-2 py-1.5 text-sm font-semibold text-[#4a4a4a]">
                                    .{d.mall_host}
                                </span>
                            </div>
                        </div>
                        <Btn tone="solid" disabled={busy === "sub"} testid="domain-save-subdomain"
                            onClick={() => run("sub", () => api.put("/store/domain", { subdomain: sub }))}>
                            {busy === "sub" ? <Loader2 size={11} className="animate-spin" /> : <Globe size={11} />} Save address
                        </Btn>
                        <a href={d.free_url} target="_blank" rel="noreferrer" data-testid="domain-free-url"
                            className="text-[13px] font-semibold text-[#1a56db] hover:underline">{d.free_url}</a>
                    </div>
                </Section>

                <Section title="Your own domain" note="Already own a name? Point it here."
                    testid="domain-custom">
                    <div className="flex flex-wrap items-end gap-2">
                        <div className="min-w-[16rem]">
                            <label className="mb-1 block text-[13px] font-semibold text-[#1a1a1a]">Hostname</label>
                            <input className={inputCls} value={host} placeholder="shop.yourname.com"
                                onChange={(e) => setHost(e.target.value)} data-testid="domain-host" />
                        </div>
                        <Btn tone="solid" disabled={busy === "host"} testid="domain-save-host"
                            onClick={() => run("host", () => api.put("/store/domain", { custom_domain: host }))}>
                            {busy === "host" ? <Loader2 size={11} className="animate-spin" /> : <Globe size={11} />} Save domain
                        </Btn>
                        {d.custom_domain && (
                            <Btn disabled={busy === "check"} testid="domain-verify"
                                onClick={() => run("check", () => api.post("/store/domain/verify"))}>
                                {busy === "check" ? <Loader2 size={11} className="animate-spin" /> : <RefreshCw size={11} />} Check DNS
                            </Btn>
                        )}
                    </div>
                    <p className="mt-1.5 text-[13px] text-[#4a4a4a]">
                        Just the hostname — no http://, no trailing slash. Your free address keeps working the whole time.
                    </p>
                </Section>

                {d.records.length > 0 && (
                    <Section title="Paste these at your registrar"
                        note={`Wherever you bought ${d.custom_domain} — GoDaddy, Namecheap, Squarespace, Cloudflare.`}
                        testid="domain-records">
                        <table className="w-full border-collapse text-sm">
                            <thead>
                                <tr className="border-b border-[#d0d0d0] text-left text-[11px] font-semibold uppercase tracking-[0.06em] text-[#4a4a4a]">
                                    <th className="py-1 pr-3">Type</th><th className="py-1 pr-3">Name</th>
                                    <th className="py-1 pr-3">Value</th><th className="py-1">TTL</th>
                                </tr>
                            </thead>
                            <tbody>
                                {d.records.map((r, i) => (
                                    <tr key={`${r.type}-${r.value}`} className="border-b border-[#d0d0d0]"
                                        data-testid={`domain-record-${i}`}>
                                        <td className="py-1.5 pr-3 font-bold text-[#1a1a1a]">{r.type}</td>
                                        <td className="py-1.5 pr-3 font-mono text-[#1a1a1a]">{r.name}</td>
                                        <td className="py-1.5 pr-3 font-mono text-[#1a1a1a]">
                                            <button onClick={() => copy(r.value)} data-testid={`domain-copy-${i}`}
                                                className="inline-flex items-center gap-1 hover:underline">
                                                {r.value} <Copy size={10} className="text-[#4a4a4a]" />
                                            </button>
                                        </td>
                                        <td className="py-1.5 text-[#4a4a4a]">{r.ttl}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        <div className="mt-2 space-y-1 text-[13px] text-[#1a1a1a]" data-testid="domain-dns-state">
                            {d.checked_at && (
                                <p className="flex items-center gap-1.5">
                                    <Clock size={12} className="text-[#4a4a4a]" /> Last checked {when(d.checked_at)}
                                    {d.found.length ? ` — found ${d.found.join(", ")}` : d.dns_error ? ` — ${d.dns_error}` : ""}
                                </p>
                            )}
                            {d.verified_at && (
                                <p className="flex items-center gap-1.5 font-semibold text-[#067647]" data-testid="domain-verified">
                                    <CheckCircle2 size={12} /> Pointing here since {when(d.verified_at)}
                                </p>
                            )}
                            {d.emailed_at && <p className="text-[#4a4a4a]">These records were emailed to you {when(d.emailed_at)}.</p>}
                        </div>
                    </Section>
                )}

                <Section title="How it goes live" note="Three steps, and we do the last one."
                    testid="domain-steps">
                    <ol className="space-y-2.5">
                        {(d.steps || []).map(([head, body], i) => (
                            <li key={head} className="flex gap-2.5" data-testid={`domain-step-${i}`}>
                                <span className="mt-0.5 grid h-4 w-4 shrink-0 place-items-center border border-[#1a1a1a] bg-[#1a1a1a] text-[11px] font-bold text-white bo-inv">
                                    {i + 1}
                                </span>
                                <span>
                                    <span className="block text-[13px] font-bold text-[#1a1a1a]">{head}</span>
                                    <span className="block text-[13px] leading-snug text-[#4a4a4a]">{body}</span>
                                </span>
                            </li>
                        ))}
                    </ol>
                    <p className="mt-3 flex items-start gap-1.5 text-[13px] text-[#4a4a4a]">
                        <ShieldCheck size={13} className="mt-0.5 shrink-0" />
                        HTTPS is included — we handle the certificate. Stuck? Open the live chat in the corner
                        and we'll read your DNS panel with you.
                    </p>
                </Section>
            </div>
        </BackOffice>
    );
}

/** Simple (8.7): the domain lives inside Branding. */
export default function StoreDomain() {
    const { isSimple } = useSiteMode();
    return isSimple ? <Navigate to="/account/store?tab=branding" replace /> : <ClassicStoreDomain />;
}
